/**
 * PlanFeasibility — V105-7-2, the target plan's feasibility checks (ADR-0074 §5; owner decisions
 * 2026-10-09), with the round-1 three-critic findings applied. One row per required check: a leading
 * status chip (status never by colour alone), what it covers, and the one action that advances it.
 *
 *   - Not defined / in review (decision 2): compact row (title, services, "Waiting on GAC Pro review");
 *     "Choose another option" sits once in the part head, not on every row.
 *   - Not run, expired or revoked: "Run this check" opens the check's inputs (typed, prefilled from
 *     the last issue) and issues the signed bundle. Readers ask an admin or the lead.
 *   - Issued: "How to run it" opens the hand-off panel (GitHub Actions / Collector / Manual runner) with
 *     the token and the runner code made right there by owners / admins; "Issued <date> · expires
 *     <date>". Owners / admins can "Revoke and issue again" (reason required, inputs prefilled); a lead
 *     asks an admin.
 *   - A result we could not attribute: the exact fix (id-token: write + --identity, or the manual runner).
 *   - Failed: "What failed" lists the failing probes in plain words, with the CI run link, then
 *     "Run it again" and "Choose another option".
 *   - Passed: the date; nothing to do.
 *
 * Authorization is the server's: issuing = plan writers (owner / admin or the program lead, `can_issue`);
 * revoking, the CI token and the runner code = owner / admin (`can_lock` stands for the owner / admin
 * role here). Display names only; keys and hashes stay out of the row.
 */
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, Input } from '@aigovops/design-system';
import type { PlanGapAction, PlanSpike, VerificationResultSummary } from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { getPlanResults, issueSpike, revokeBundle } from '../../lib/infra-plan-api';
import { fmtDate } from '../../lib/format';
import { AskAdmin } from '../AskAdmin';
import { Link, useLocation } from 'react-router-dom';
import { runnerGap } from '../../lib/runner-release';
import { trustWorkflowHref } from '../../lib/ci-identities-api';
import { SpikeHandoff, type HandoffSecrets } from './SpikeHandoff';

type Status = PlanSpike['status'];
type Tone = 'ok' | 'bad' | 'act' | 'wait';
const CHIP: Record<Status, { text: string; tone: Tone; chip: 'ok' | 'bad' | 'warn' | 'info' | undefined }> = {
  not_defined: { text: 'Not defined', tone: 'wait', chip: undefined },
  definition_pending: { text: 'Not defined', tone: 'wait', chip: undefined },
  target_unresolved: { text: 'Not resolved', tone: 'wait', chip: undefined },
  not_issued: { text: 'Not run', tone: 'act', chip: 'warn' },
  issued: { text: 'Waiting', tone: 'wait', chip: 'info' },
  awaiting_verified_result: { text: 'Not confirmed', tone: 'act', chip: 'warn' },
  passed: { text: 'Passed', tone: 'ok', chip: 'ok' },
  failed: { text: 'Failed', tone: 'bad', chip: 'bad' },
  expired: { text: 'Expired', tone: 'act', chip: 'warn' },
  revoked: { text: 'Revoked', tone: 'bad', chip: 'bad' },
};
export const RUNNABLE = new Set<Status>(['not_issued', 'failed', 'expired', 'revoked', 'awaiting_verified_result']);
export const WAITING_ON_RESULT = new Set<Status>(['issued']);
const INPUT_LABEL: Record<string, string> = { https_url: 'URL', resource_id: 'Resource ID' };
const INPUT_ERROR: Record<string, string> = { https_url: 'Enter the https URL of your sandbox endpoint.', resource_id: 'Enter the resource ID.' };
/** Probe types in plain words (verification-probes.ts). */
const PROBE_LABEL: Record<string, string> = {
  health: 'The endpoint check', hook_e2e: 'Your end-to-end test', hook_timed: 'The timed step', ksi_check: 'A security check',
  residency: 'Where the data is kept', egress_allowlist: 'Outbound traffic', table_parity: 'The data copy',
};

export const isBlocked = (s: PlanSpike) => s.status === 'not_defined' || s.status === 'definition_pending' || s.status === 'target_unresolved';
/** ADR-0074 note 2026-10-09: an unresolved move reads as its plain reason ("<service>'s replacement isn't resolved yet"). */
export const spikeTitle = (s: PlanSpike) => (s.status === 'target_unresolved' && s.unresolved ? s.unresolved.message : null) ?? s.title ??`Feasibility check for ${s.services.map((x) => x.label || x.name).join(', ') || 'this option'}`;

export interface PlanFeasibilityHandle {
  /** The head primary "Run feasibility checks": open the first runnable check's inputs (never toggles closed). */
  openFirstRunnable(): void;
  /** The head primary "Get the checks run": open and focus the first issued check's hand-off panel. */
  focusFirstIssued(): void;
}

const errBody = (e: unknown) => (e instanceof ApiError && e.body && typeof e.body === 'object' ? e.body as { error?: string; detail?: string; field?: string } : {});

export const PlanFeasibility = forwardRef<PlanFeasibilityHandle, {
  slug: string; planId: string; label: string; spikes: PlanSpike[];
  /** Plan writers: owner / admin or the program lead. */
  canIssue: boolean;
  /** Owner / admin: revoke, the CI token, the runner registration code. */
  canManage: boolean;
  /** Owner / admin may choose another strategy option; others ask. */
  canDecide: boolean;
  onChooseOther: () => void;
  /** Reload the checks (await it before moving focus). */
  onChanged: () => Promise<void>;
  onIssued: (title: string) => void;
  onError: (text: string) => void;
  /** While an inputs form is open the head primary steps aside (one primary). */
  onFormOpenChange?: (open: boolean) => void;
  /** Bundle ids handed off in this session (instructions copied or emailed, or the check downloaded). */
  handedOff: ReadonlySet<string>;
  onHandedOff: (bundleId: string) => void;
  /** The action that resolves an unresolved move (the plan view's gap actions), when it has one. */
  renderAction?: (a: PlanGapAction, who: string) => ReactNode;
}>(function PlanFeasibility({ slug, planId, label, spikes, canIssue, canManage, canDecide, onChooseOther, onChanged, onIssued, onError, onFormOpenChange, handedOff, onHandedOff, renderAction }, ref) {
  const uid = useId();
  const location = useLocation();
  // Round 3: "Trust a workflow" brings the person back here (Settings validates it as an in-app path).
  const returnTo = `${location.pathname}${location.search}`;
  // Round 3 (F-H2): no check is issued while GAC Pro hasn't published the runner or the signer.
  const gap = runnerGap();
  const [open, setOpen] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  // R2-H3: one-time secrets by bundle id, kept here so they survive the panel collapsing or a refetch.
  const [secrets, setSecrets] = useState<Record<string, HandoffSecrets>>({});
  /** Focus after the next render: the element id (H8 / H10). */
  const pendingFocus = useRef<string | null>(null);
  const lastRow = useRef<string | null>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  const rowId = (s: PlanSpike) => `${uid}-${s.spike_key ?? `${s.entry_id}-${s.services[0]?.item_id ?? ''}`}`;
  const anyFailed = spikes.some((s) => s.status === 'failed');
  const results = useQuery({ queryKey: ['infra-plan', slug, 'results', planId], queryFn: () => getPlanResults(slug, planId), enabled: anyFailed });

  useEffect(() => { onFormOpenChange?.(!!open); }, [open, onFormOpenChange]);
  useEffect(() => {
    const id = pendingFocus.current;
    if (id) {
      const el = document.getElementById(id);
      if (el) { pendingFocus.current = null; el.scrollIntoView?.({ block: 'nearest' }); el.focus(); return; }
    }
    // H9: a refetch that unmounts the focused control returns focus to its row, not to the page.
    // A pending focus move wins; never move focus twice in one update (round-2 Medium).
    if (pendingFocus.current) return;
    const gone = lastFocused.current && !lastFocused.current.isConnected;
    if (gone && lastRow.current && (document.activeElement === document.body || !document.activeElement)) {
      lastFocused.current = null;
      document.getElementById(lastRow.current)?.focus();
    }
  });

  const startForm = (s: PlanSpike) => {
    if (!s.spike_key) return;
    setOpen(s.spike_key); setErrors({}); setRevoking(null);
    // Prefill from the inputs the last bundle was issued with (B2), without overwriting what was typed.
    setValues((m) => {
      const next = { ...m };
      for (const i of s.inputs) { const k = `${s.spike_key}:${i.name}`; if (!next[k] && s.last_inputs?.[i.name]) next[k] = s.last_inputs[i.name]!; }
      return next;
    });
    pendingFocus.current = s.inputs.length ? `${rowId(s)}-in-0` : `${rowId(s)}-issue`;
  };
  const closeForm = (s: PlanSpike) => { setOpen(null); pendingFocus.current = `${rowId(s)}-run`; };

  useImperativeHandle(ref, () => ({
    openFirstRunnable() {
      // An unconfirmed result needs its fix, not new inputs (round-2 Medium): skip it here.
      const s = spikes.find((x) => RUNNABLE.has(x.status) && x.status !== 'awaiting_verified_result' && !!x.spike_key);
      if (s) startForm(s);
    },
    focusFirstIssued() {
      const s = spikes.find((x) => (x.status === 'issued' || x.status === 'awaiting_verified_result') && x.bundle?.status === 'active');
      if (!s?.spike_key) return;
      setHandoff(s.spike_key);
      pendingFocus.current = `${rowId(s)}-handoff-h`;
      if (handoff === s.spike_key) document.getElementById(pendingFocus.current)?.focus();
    },
  }));

  const issue = async (s: PlanSpike, ev: FormEvent) => {
    ev.preventDefault();
    if (!s.spike_key || busy) return;
    const errs: Record<string, string> = {};
    for (const i of s.inputs) {
      const v = values[`${s.spike_key}:${i.name}`]?.trim() ?? '';
      if (!v) errs[i.name] = INPUT_ERROR[i.type] ?? 'Enter a value.';
      else if (i.type === 'https_url' && !/^https:\/\/[^\s/]+/.test(v)) errs[i.name] = INPUT_ERROR.https_url!;
    }
    setErrors(errs);
    const first = s.inputs.findIndex((i) => errs[i.name]);
    if (first >= 0) { document.getElementById(`${rowId(s)}-in-${first}`)?.focus(); return; }
    setBusy(s.spike_key);
    try {
      await issueSpike(slug, planId, s.spike_key, Object.fromEntries(s.inputs.map((i) => [i.name, values[`${s.spike_key}:${i.name}`]!.trim()])));
      setOpen(null);
      // H8: the issued row's hand-off panel opens and takes focus once the row re-renders.
      setHandoff(s.spike_key);
      pendingFocus.current = `${rowId(s)}-handoff-h`;
      onIssued(spikeTitle(s));
      await onChanged();
    } catch (e) {
      const b = errBody(e);
      const idx = b.field ? s.inputs.findIndex((i) => i.name === b.field) : -1;
      if (idx >= 0) {
        // The server named the input: mark that field, keep the form open.
        setErrors({ [s.inputs[idx]!.name]: INPUT_ERROR[s.inputs[idx]!.type] ? `${INPUT_ERROR[s.inputs[idx]!.type]} ${b.error ?? ''}`.trim() : (b.error ?? 'Check this value.') });
        document.getElementById(`${rowId(s)}-in-${idx}`)?.focus();
      } else onError(b.detail ?? b.error ?? 'The feasibility check could not be issued.');
    } finally { setBusy(null); }
  };

  const revokeAndReissue = async (s: PlanSpike, ev: FormEvent) => {
    ev.preventDefault();
    if (!s.bundle || busy) return;
    if (!reason.trim()) { setReasonError('Say why, e.g. the sandbox URL was wrong.'); document.getElementById(`${rowId(s)}-reason`)?.focus(); return; }
    setBusy(`revoke:${s.spike_key}`);
    try {
      await revokeBundle(slug, planId, s.bundle.bundle_id, reason.trim());
      setRevoking(null); setReason(''); setHandoff(null);
      await onChanged();
      // The row is runnable again: open its inputs, prefilled.
      startForm({ ...s, status: 'revoked' });
    } catch (e) {
      const b = errBody(e);
      onError(b.error === 'already_revoked' ? 'That check was already revoked.' : (b.detail ?? b.error ?? 'The check could not be revoked.'));
    } finally { setBusy(null); }
  };

  const failedResult = (s: PlanSpike): VerificationResultSummary | null =>
    (s.result ? results.data?.results.find((r) => r.result_id === s.result!.result_id) : null) ?? results.data?.results.find((r) => r.bundle_id === s.bundle?.bundle_id) ?? null;

  const choose = (s: PlanSpike, key: string) => (canDecide
    ? <Button compact variant="ghost" aria-label={`Choose another option · ${spikeTitle(s)}`} onClick={onChooseOther}>Choose another option</Button>
    : <AskAdmin key={key} slug={slug} what="choose another option" subject={`${label}: ${spikeTitle(s)}`} compact />);

  if (!spikes.length) return <p className="gp-dim">No feasibility check is needed: nothing in this plan moves to a new option that needs one.</p>;
  return (
    <ul className="gp-pv-gaps" aria-label="Feasibility checks">
      {spikes.map((s) => {
        const id = rowId(s);
        const key = s.spike_key ?? id;
        const title = spikeTitle(s);
        const who = s.title ? s.services.map((x) => x.name).join(', ') : '';
        const chip = CHIP[s.status];
        const runnable = RUNNABLE.has(s.status) && !!s.spike_key;
        // Round 3 (F-H1): an unconfirmed result is never "Run it again" at the row level.
        const showRun = runnable && s.status !== 'awaiting_verified_result';
        const why = s.status === 'awaiting_verified_result' ? (s.unconfirmed?.reason ?? null) : null;
        const activeBundle = s.bundle && s.bundle.status === 'active' ? s.bundle : null;
        const canHandoff = !!activeBundle && (s.status === 'issued' || s.status === 'awaiting_verified_result');
        const sub = s.status === 'passed' && s.result ? `Passed ${fmtDate(s.result.received_at)}`
          : s.status === 'failed' && s.result ? `Failed ${fmtDate(s.result.received_at)}`
          : s.status === 'issued' && s.bundle ? `Issued ${fmtDate(s.bundle.issued_at)} · expires ${fmtDate(s.bundle.expires_at)}${handedOff.has(s.bundle.bundle_id) ? ' · handed off' : ''}`
          : s.status === 'awaiting_verified_result' ? (why === 'identity_trusted_rerun' ? 'Workflow trusted · run it again from GitHub Actions to count it'
            : why === 'identity_not_trusted' ? "Not confirmed · this workflow isn't trusted yet" : "The result came in, but we couldn't confirm who ran it.")
          : s.status === 'expired' && s.bundle ? `Expired ${fmtDate(s.bundle.expires_at)}`
          : s.status === 'revoked' ? 'Revoked · issue it again'
          : s.status === 'not_issued' ? 'Not run yet' : '';
        const runLabel = s.status === 'not_issued' ? 'Run this check' : 'Run it again';
        const fr = s.status === 'failed' ? failedResult(s) : null;
        return (
          <li key={key} id={id} tabIndex={-1} className="gp-pv-gap gp-pv-check" data-check="" data-tone={chip.tone} onFocusCapture={(e) => { lastRow.current = id; lastFocused.current = e.target as HTMLElement; }}>
            <span className="gp-pv-check-main">
              <span className="gp-cb-chip" data-tone={chip.chip}>{chip.text}</span>{' '}
              <b>{title}</b>{who ? <span className="gp-dim">{` · ${who}`}</span> : null}
              {sub ? <span className="gp-pv-check-sub gp-dim">{sub}</span> : null}
            </span>
            <span className="gp-actions">
              {isBlocked(s) && s.waiting_on_gacpro ? <span className="gp-cb-tag">Waiting on GAC Pro review</span> : null}
              {s.status === 'target_unresolved' && s.unresolved?.action && renderAction ? renderAction(s.unresolved.action, s.services.map((x) => x.name).join(', ')) : null}
              {canHandoff ? (
                <Button compact variant={s.status === 'issued' ? 'secondary' : 'ghost'} id={`${id}-howto`} aria-expanded={handoff === s.spike_key} aria-label={`How to run it · ${title}`}
                  onClick={() => { const opening = handoff !== s.spike_key; setHandoff(opening ? s.spike_key : null); if (opening) pendingFocus.current = `${id}-handoff-h`; }}>How to run it</Button>
              ) : null}
              {s.status === 'issued' && activeBundle && !gap ? (canManage
                ? <Button compact variant="ghost" id={`${id}-revoke`} aria-expanded={revoking === s.spike_key} aria-label={`Revoke and issue again · ${title}`}
                    onClick={() => { const opening = revoking !== s.spike_key; setRevoking(opening ? s.spike_key : null); setReason(''); setReasonError(''); setOpen(null); if (opening) pendingFocus.current = `${id}-reason`; }}>Revoke and issue again</Button>
                : canIssue ? <AskAdmin slug={slug} what="revoke the check and issue it again" subject={`${label}: ${title}`} compact /> : null) : null}
              {showRun && gap ? <span className="gp-cb-tag">{gap === 'signer' ? 'Waiting on GAC Pro · bundle signing' : 'Waiting on GAC Pro · runner release'}</span> : null}
              {showRun && !gap ? (canIssue
                ? <Button compact variant={s.status === 'failed' || s.status === 'awaiting_verified_result' ? 'secondary' : 'ghost'} id={`${id}-run`} aria-expanded={open === s.spike_key} aria-label={`${runLabel} · ${title}`}
                    onClick={() => (open === s.spike_key ? closeForm(s) : startForm(s))}>{runLabel}</Button>
                : <AskAdmin slug={slug} what="run the feasibility check" subject={`${label}: ${title}`} lead="An owner, admin or the program lead" compact />) : null}
              {s.status === 'failed' ? choose(s, `choose-${key}`) : null}
            </span>

            {why === 'identity_trusted_rerun' ? null : why === 'identity_not_trusted' && s.unconfirmed ? (
              <span className="gp-actions gp-pv-detail">
                {canManage
                  ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="secondary" to={trustWorkflowHref(slug, s.unconfirmed.claimed, returnTo)} aria-label={`Trust this workflow · ${title}`}>Trust this workflow</Link>
                  : <AskAdmin slug={slug} what={`trust our CI workflow${s.unconfirmed.claimed ? ` (${s.unconfirmed.claimed.repository}${s.unconfirmed.claimed.workflow_path ? ` · ${s.unconfirmed.claimed.workflow_path}` : ''})` : ''} in Settings → Security → CI identities`} subject={`${label}: trust our CI workflow`} compact />}
              </span>
            ) : s.status === 'awaiting_verified_result' ? (
              <details className="gp-cb-why gp-pv-detail">
                <summary>How to fix it<span className="sr-only">{` · ${title}`}</span></summary>
                <span>Give the job <code>permissions: id-token: write</code> and run with <code>--identity --sign-result</code>.</span>
                <span>Then trust the workflow in Settings → Security → CI identities{' '}
                  {canManage ? <Link to={trustWorkflowHref(slug, null, returnTo)} aria-label={`Trust a workflow · ${title}`}>Trust a workflow</Link>
                    : <AskAdmin slug={slug} what="trust our CI workflow in Settings → Security → CI identities" subject={`${label}: trust our CI workflow`} compact />}
                  {' '}or use the manual runner.</span>
                {runnable && canIssue && !gap ? (
                  <span className="gp-actions"><Button compact variant="ghost" id={`${id}-run`} aria-expanded={open === s.spike_key} aria-label={`Issue the check again · ${title}`}
                    onClick={() => (open === s.spike_key ? closeForm(s) : startForm(s))}>Issue the check again</Button></span>
                ) : null}
              </details>
            ) : null}

            {s.status === 'failed' ? (
              <details className="gp-cb-why gp-pv-detail">
                <summary>What failed<span className="sr-only">{` · ${title}`}</span></summary>
                {results.isLoading ? <span>Loading the result…</span> : !fr ? <span>The result details could not be loaded.</span> : (
                  <>
                    <ul className="gp-pv-list">
                      {fr.probes.filter((p) => p.status !== 'pass').map((p) => (
                        <li key={p.probe_id}><b>{`${PROBE_LABEL[p.probe_type] ?? 'A check'} ${p.status === 'error' ? "couldn't run" : 'failed'}`}</b>
                          {p.details?.length ? <ul className="gp-pv-files">{p.details.map((d, i) => <li key={i}>{d}</li>)}</ul> : null}</li>
                      ))}
                    </ul>
                    {fr.ci_run_url && /^https:\/\//.test(fr.ci_run_url) ? <a href={fr.ci_run_url} target="_blank" rel="noreferrer">Open the CI run</a> : null}
                  </>
                )}
              </details>
            ) : null}

            {canHandoff && handoff === s.spike_key && activeBundle ? (
              <SpikeHandoff slug={slug} planId={planId} label={label} title={title} bundleId={activeBundle.bundle_id} signerIdentity={activeBundle.signer_identity ?? null}
                canManage={canManage} headingId={`${id}-handoff-h`} returnTo={returnTo} onHandedOff={() => onHandedOff(activeBundle.bundle_id)}
                secrets={secrets[activeBundle.bundle_id] ?? {}} onSecret={(kind, value) => setSecrets((m) => ({ ...m, [activeBundle.bundle_id]: { ...m[activeBundle.bundle_id], [kind]: value } }))} />
            ) : null}

            {revoking === s.spike_key && activeBundle && canManage ? (
              <form className="gp-pv-form" noValidate aria-label={`Revoke and issue again · ${title}`} onSubmit={(e) => void revokeAndReissue(s, e)}
                onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setRevoking(null); pendingFocus.current = `${id}-revoke`; } }} data-esc-owner="">
                <Field id={`${id}-reason`} label="Why revoke it" required error={reasonError} hint="The check stops working; you then issue it again with corrected inputs.">
                  <Input value={reason} onChange={(e) => { setReason(e.currentTarget.value); setReasonError(''); }} maxLength={500} />
                </Field>
                <span className="gp-actions">
                  <Button compact variant="danger" type="submit" aria-busy={busy === `revoke:${s.spike_key}` || undefined}>{busy === `revoke:${s.spike_key}` ? 'Revoking…' : 'Revoke and issue again'}</Button>
                  <Button compact variant="ghost" type="button" onClick={() => { setRevoking(null); pendingFocus.current = `${id}-revoke`; }}>Cancel</Button>
                </span>
              </form>
            ) : null}

            {open === s.spike_key && runnable && canIssue ? (
              <form className="gp-pv-form" onSubmit={(e) => void issue(s, e)} noValidate aria-label={`Inputs · ${title}`} data-esc-owner=""
                onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeForm(s); } }}>
                {s.question ? <p className="gp-dim">{s.question}</p> : null}
                {s.inputs.map((i, n) => (
                  <Field key={i.name} id={`${id}-in-${n}`} label={INPUT_LABEL[i.type] ?? (i.description || 'Value')} hint={i.description} required error={errors[i.name]}>
                    <Input type={i.type === 'https_url' ? 'url' : 'text'} inputMode={i.type === 'https_url' ? 'url' : undefined} value={values[`${s.spike_key}:${i.name}`] ?? ''}
                      onChange={(e) => { const v = e.currentTarget.value; setValues((m) => ({ ...m, [`${s.spike_key}:${i.name}`]: v })); setErrors((m) => ({ ...m, [i.name]: '' })); }} />
                  </Field>
                ))}
                <span className="gp-actions">
                  <Button compact variant="primary" type="submit" id={`${id}-issue`} aria-busy={busy === s.spike_key || undefined}>{busy === s.spike_key ? 'Issuing…' : 'Issue the check'}</Button>
                  <Button compact variant="ghost" type="button" onClick={() => closeForm(s)}>Cancel</Button>
                </span>
              </form>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
});
